import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import path from 'path';
import Cropper from 'cropperjs';
import { SITE_BUCKET } from '@/constants';
import { guid } from '@/mixins/guid';
import { uploader } from '@/mixins/aws/uploader/s3';
import uploadProgress from '@/mixins/aws/uploader';
import { siteBuilder } from '@/mixins/site-builder';

export default Vue.component('data-product-store-brand', {
  template: require('./template.html'),
  name: 'data-product-store-brand',
  mixins: [guid, uploader, siteBuilder],
  computed: {
    ...mapGetters(['currentSite', 'currentUser', 'activeSiteType']),
    logo() {
      return this.currentSite.dynamic.products.logo;
    },
    fileName() {
      return path.basename(this.logo);
    },
  },
  components: {
    uploadProgress,
  },
  data() {
    return {
      loading: false,
      imgLoading: false,
      imageFiles: [],
      showFiles: false,
      tempPath: '',
      bucketPath: null,
      prefix: null,
      thumbnails: null,
      confirmDeleteImg: false,
      activeImage: null,
      hoverDel: false,
      activeIndex: null,
      cropper: null,
      localFile: null,
      targetFile: null,
      targetName: '',
      openCropper: false,
      initialEdits: {
        logo: null,
      },
      activeAspect: {},
      aspectRatios: {
        raw: {
          display: 'Raw Image (Ignore Cropper)',
          ratio: 0,
        },
        free: {
          display: 'Free',
        },
        shortRect: {
          ratio: 4 / 5,
          height: 750,
          display: '4:5',
          width: 600,
        },
        tallRect: {
          ratio: 2 / 3,
          height: 750,
          display: '2:3',
          width: 500,
        },
        square: {
          ratio: 1 / 1,
          height: 600,
          display: '1:1',
          width: 600,
        },
        smallWideRect: {
          ratio: 4 / 3,
          display: '4:3',
          height: 600,
          width: 800,
        },
        wideRect: {
          ratio: 16 / 9,
          height: 700,
          display: '16:9',
          width: 1244,
        },
      },
    };
  },
  watch: {
    openCropper(open) {
      if (open) {
        setTimeout(() => this.setCropper(), 300);
      }
    },
  },
  created() {
    this.setInitialValues();
  },
  methods: {
    setInitialValues() {
      // for s3 assets
      this.bucketPath = `_droplet-assets/${this.activeSiteType}/brand/`;
      this.prefix = `drz_${this.activeSiteType}_`;
      this.thumbnails = `_droplet-assets/${this.activeSiteType}/brand/_thumbnails/`;
      this.initialEdits.logo = this.logo;
      this.activeAspect = {
        height: 600,
        width: 600,
        ratio: 1 / 1,
        display: '1:1',
      };
    },
    openUpload() {
      this.$refs.uploadFile.click();
    },
    openLocalImg(e) {
      this.$validator.validateAll().then(async (result) => {
        // run only if file types and dimensions are correct
        if (result) {
          const evt = e;
          let newFile = [...evt.target.files];
          if (newFile.length > 0) {
            try {
              newFile = newFile[0];
              this.targetFile = newFile;
              const base64File = await this.fileToBase64(newFile);
              const img = new Image();
              img.src = base64File;
              await new Promise((r) => { img.onload = () => r(); });
              this.openCropper = base64File;
              // clean up the file name for s3
              let fileName = this.replaceExt({ file: path.basename(newFile.name), ext: '.png' });
              fileName = `${this.prefix}${_.trim(fileName)}`;
              this.localFile = _.replace(fileName, /\s+/g, '_');
              // original target name
              let targetName = path.basename(newFile.name);
              targetName = `${this.prefix}${_.trim(targetName)}`;
              this.targetName = _.replace(targetName, /\s+/g, '_');
            } catch (err) {
              this.$log.error(err);
              await this._reportError({
                error: err,
                fn: 'item openLocalImg',
              });
            }
          }
          // reset value to null so we can get the "change" event to run
          // in any case. Even when the user selects the same file
          evt.target.value = null;
        }
      });
    },
    setCropper() {
      if (this.activeAspect.ratio !== 0) {
        const image = this.$refs.imageCropper;
        this.cropper = new Cropper(image, {
          aspectRatio: this.activeAspect.ratio,
          dragMode: 'move',
          autoCropArea: 1,
          zoomable: false,
        });
      }
    },
    setAspect(aspect) {
      this.activeAspect = aspect;
      if (this.cropper) {
        this.cropper.destroy();
      }
      this.cropper = null;
      this.setCropper();
    },
    destroyCropper() {
      this.openCropper = false;
      this.localFile = null;
      if (this.cropper) {
        this.cropper.destroy();
        this.cropper = null;
      }
    },
    getBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob(blob => resolve(blob));
      });
    },
    async uploadCropped() {
      try {
        let file = this.targetFile;
        let name = this.targetName;
        if (this.activeAspect.ratio !== 0) {
          let { width, height } = this.cropper.getContainerData();
          if (this.activeAspect.width) {
            width = this.activeAspect.width;
          }

          if (this.activeAspect.height) {
            height = this.activeAspect.height;
          }
          const canvas = this.cropper.getCroppedCanvas({ width, height, imageSmoothingQuality: 'high' });
          file = await this.getBlob(canvas);
          name = this.localFile;
        }
        // send off to our s3 upload mixin
        const filePath = `/${this.bucketPath}${name}`;
        await this.uploadFile({
          file: `${this._sitePath}${filePath}`,
          body: file,
          thumbnail: {
            width: 35,
            srcFile: file,
            name,
            bucketPath: `${this._sitePath}/${this.thumbnails}`,
          },
        });
        this.$store.dispatch('setGlobalValue', {
          item: this.currentSite.dynamic.products,
          key: 'logo',
          value: filePath,
        });
        this.destroyCropper();
        this.targetFile = null;
        this.targetName = '';
      } catch (err) {
        if (!err.message.match(/aborted/gi)) {
          this.$log.error(err);
          await this._reportError({
            error: err,
            fn: 'item uploadCropped',
          });
        }
      } finally {
        if (this.showFiles) {
          setTimeout(() => {
            this.fetchImageFiles();
          }, 500);
        }
      }
    },
    resetCropper() {
      this.cropper.reset();
    },
    rotateImage(direction) {
      const deg = direction === 'clockwise' ? 90 : -90;
      this.cropper.rotate(deg);
    },
    toggleBrowse() {
      this.showFiles = !this.showFiles;
      if (this.showFiles) {
        this.fetchImageFiles();
      }
    },
    async fetchImageFiles() {
      try {
        this.imgLoading = true;
        const assetsFolder = `/_droplet-assets/${this.activeSiteType}/brand`;
        const files = await this._s3fetchFiles(`${this._sitePath}/${this.thumbnails}`);
        this.imageFiles = files.map(f => `${assetsFolder}${f.split(assetsFolder)[1]}`);
      } catch (err) {
        this.$log.error('error: ', err);
        this.showFiles = false;
        await this._reportError({
          error: err,
          fn: 'item fetchImageFiles',
        });
      } finally {
        this.imgLoading = false;
      }
    },
    async deleteImg() {
      this.confirmDeleteImg = false;
      // prep notifcation type
      const notif = {
        id: this.guid(),
        time: 2000,
      };
      const file = path.basename(this._getFromThumb(this.activeImage));
      const thumb = path.basename(this.activeImage);
      try {
        this.imgLoading = true;
        // delete original from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.bucketPath}${file}`,
        });
        // delete thumbnail from s3
        await this._s3deleteFile({
          key: `${this._fullSiteFolder}/${this.thumbnails}${thumb}`,
        });
        // set for notifications
        notif.type = 'success';
        notif.message = `Successfully deleted file: ${path.basename(this.activeImage)}`;
      } catch (err) {
        this.$log.error('error: ', err);
        // set for notifications
        notif.type = 'error';
        notif.message = `${err.message || err}. This has been reported.`;
        await this._reportError({
          error: err,
          fn: 'item deleteImg',
        });
      } finally {
        // send off to notifications
        this.$store.dispatch('addNotification', notif);
        // kill loader & refetch files
        this.imgLoading = false;
        this.fetchImageFiles();
      }
      // if deleting image that is being used, reset it to default
      if (this.logo === `/${this.bucketPath}${file}`) {
        this.setImg(false);
      }
    },
    async setImg(f) {
      let file;
      if (!f) {
        file = f;
      } else {
        file = `/${this.bucketPath}${this._getFromThumb(path.basename(f))}`;
      }
      this.$store.dispatch('setGlobalValue', {
        item: this.currentSite.dynamic.products,
        key: 'logo',
        value: file,
      });
    },
    matchImg(file) {
      const f = this._getFromThumb(path.basename(file));
      const media = path.basename(this.logo);
      return f === media;
    },
    confirmDelete(file) {
      this.activeImage = file;
      this.confirmDeleteImg = true;
    },
    async saveChanges() {
      this.loading = true;
      const notif = {
        id: this.guid(),
        type: 'success',
        time: 2000,
        message: 'Store changes saved!',
      };
      try {
        if (!this.currentSite.dynamic.products.bucketFolder) {
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite.dynamic.products,
            key: 'bucketFolder',
            value: `${SITE_BUCKET}/${this._uploadFolder}/sites/${this.currentSite.name}`,
          });
        }
        if (!this.currentSite.dynamic.products.storeEmail) {
          // TODO this may need to be moved if store support gets own tab
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite.dynamic.products,
            key: 'storeEmail',
            value: this.currentUser.email,
          });
        }
        if (!this.currentSite.dynamic.products.emails) {
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite.dynamic.products,
            key: 'emails',
            value: [],
          });
        }
        await this._SEND([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        }]);
        // we also need to regenerate the site in this instance
        await this.callBuilder({
          publish: 'site',
          site: {
            _id: this.currentSite._id,
          },
        });
        this.initialEdits.logo = this.logo;
      } catch (e) {
        this.$log.error(e.message || e);
        notif.type = 'error';
        notif.message = e.message || e;
        await this._reportError({
          error: e,
          fn: 'saveChanges store brand',
        });
      } finally {
        this.loading = false;
        this.$store.dispatch('addNotification', notif);
      }
    },
    cancelChanges() {
      this.$store.dispatch('setGlobalValue', {
        item: this.currentSite.dynamic.products,
        key: 'logo',
        value: this.initialEdits.logo,
      });
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    startCase(text) {
      return _.startCase(text);
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/dynamic-data/store/brand/index.js